import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";

import WalletService from "../../services/WalletService";
import { validateTransferInput, parseTransferError } from "../../utils/transferErrorHandler";

import "./ProfileModals.css";

const TransferModal = ({ isOpen, onClose, userId, token, fromWalletId, currency, balance, onTransferred }) => {
  const { t } = useTranslation();
  // Transfer form state]
  const [toUserEmail, setToUserEmail] = useState("");
  const [toWalletId, setToWalletId] = useState("");
  const [amount, setAmount] = useState("");

  // UI state
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);

  const resetState = () => {
    setToWalletId("");
    setAmount("");
    setError("");
    setSuccess("");
    setSending(false);
  };

  useEffect(() => {
    if (isOpen) resetState();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const validationError = validateTransferInput({
      toUserEmail,
      amount,
      fromUserId: userId,
      balance,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSending(true);
      await WalletService.transferUser({
        from_user_id: userId,
        to_user_id: toUserEmail.trim(),
        amount: Number(amount),
        currency,
      });
      setSuccess(t("profile.transferSuccess"));
      onTransferred?.();
    } catch (err) {
      const friendlyError = parseTransferError(err);
      setError(friendlyError);
    } finally {
      setSending(false);
    }
  };

  const isButtonDisabled = sending || !toUserEmail.trim() || !amount || Number(amount) <= 0 || balance <= 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={sending ? () => {} : onClose}
      title={t("profile.transferFunds")}
      actions={
        <>
          <SecondButton onClick={onClose} disabled={sending}>
            {t("common.cancel")}
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={isButtonDisabled}>
            {sending ? t("profile.transferring") : t("contact.send")}
          </MainButton>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="profile-modal-form">
        {/* Destination wallet ID */}
        <label>
          {t("profile.toUserEmail")}
          <MainInput
            type="email"
            value={toUserEmail}
            onChange={(e) => {
              setToUserEmail(e.target.value);
              setError("");
            }}
            disabled={sending}
          />
        </label>

        {/* Transfer amount */}
        <label>
          {t("profile.amount")}
          <MainInput
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError("");
            }}
            disabled={sending}
          />
        </label>

        {/* Transfer form error/success */}
        {error && (
          <div className="profile-transfer-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {success && (
          <div className="profile-transfer-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default TransferModal;
